import { Injectable } from '@angular/core';

import { ApiService, AuthService, UserService, LogService, BillingService, AppContext } from './../shared';
import { Confirm } from './../shared/models';

@Injectable()
export class ConfirmService {
  noEmail: boolean = false;

  email: string = '';
  emailError: string = '';

  password: string = localStorage.getItem('password') || '';
  passwordError: string = '';

  frontError: boolean = false;
  message: string = '';
  thanksAmount: string = '';
  thanksDate: string = '';
  status: string = '';
  type: string = '';

  constructor(public app: AppContext, private api: ApiService, private auth: AuthService, private user: UserService, private logger: LogService,
    private billing: BillingService) {
    this.init();
  }

  init() {
    this.app.clear();
    if (this.app.email.trim() === '') {
      this.type = 'no-email';
    } else {
      this.email = this.app.email;
      this.checkIfUser(this.app.email);
    }
  }

  validateEmail() {
    //let EMAIL_REGEXP = /^[-a-z0-9~!$%^&*_=+}{\'?]+(\.[-a-z0-9~!$%^&*_=+}{\'?]+)*@([a-z0-9_][-a-z0-9_]*(\.[-a-z0-9_]+)*\.(aero|arpa|biz|com|coop|edu|gov|info|int|mil|museum|name|net|org|pro|travel|mobi|[a-z][a-z])|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?$/i;
    //let EMAIL_REGEXP = /^[-a-z0-9~!$%^&*_=+}{\'?]+(\.[-a-z0-9~!$%^&*_=+}{\'?]+)*@([a-z0-9_][-a-z0-9_]*(\.[-a-z0-9_]+)*|\.(aero|arpa|biz|com|coop|edu|gov|info|int|mil|museum|name|net|org|pro|travel|mobi|[a-z][a-z])|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?$/i;
    let EMAIL_REGEXP = this.app.EMAIL_REGEXP;

    if (EMAIL_REGEXP.test(this.email)) {
      this.emailError = '';
    } else {
      this.frontError = true;
      this.emailError = 'Invalid Email';
    }
  }

  validatePassword() {
    let PASSWORD_REGEXP = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).*$/;

    if (PASSWORD_REGEXP.test(this.password) && this.password.length >= 8) {
      this.passwordError = '';
    } else if (!PASSWORD_REGEXP.test(this.password) && this.password.length >= 8) {
      this.frontError = true;
      this.passwordError = 'Invalid Password';
    } else if (PASSWORD_REGEXP.test(this.password) && this.password.length < 8) {
      this.frontError = true;
      this.passwordError = 'Invalid Password';
    } else {
      this.frontError = true;
      this.passwordError = 'Invalid Password';
    }
  }

  checkIfUser(email: string) {
    this.logger.log('confirm.service', 'checkIfUser', email);

    if (this.status !== 'confirming') {
      this.status = 'confirming';
      this.user.checkIfUser(email).then(result => {
        // is a user, resetting password
        this.status = '';
        this.type = 'forgot-password';
      }).catch(err => {
        // not a user, must be confirming a registration
        this.status = '';
        this.type = 'register';
      });
    }
  }

  reset(email: string, code: string, password: string) {
    this.logger.log('confirm.service', 'reset code', code);
    if (this.status !== 'resetting') {
      this.status = 'resetting';
      this.auth.confirmPassword(email, code, password).then(result => {
        this.logger.log('confirm.service', 'reset result', result);
        this.status = 'reset';
        this.auth.login(email, password).then(loginResult => {
          this.user.confirmUser(email).then((confirmResult) => {
            this.user.getProfile();
          });
        });
      }).catch(err => {
        this.logger.log('confirm.service', 'reset error', err);
        this.onError(err);
      });
    }
  }

  confirm(model: Confirm) {
    this.logger.log('confirm.service', 'confirm code', model.code);

    if (this.status !== 'confirming') {
      this.status = 'confirming';

      this.user.checkIfUser(model.email).then(result => {
        // is a user, resetting password
        this.status = '';
        this.type = 'forgot-password';
      }).catch(err => {
        // not a user, confirming a registration
        this.auth.confirmEmail(model.code, model.email).then(result => {
          // success, ask user to login
          this.logger.log('confirm.service', 'confirm result', result);
          this.status = 'confirmed';
        }).catch(err => {
          // fail, prompt user to resend
          this.type = 'register';
          this.email = model.email;
          this.logger.log('confirm.service', 'confirm error', err);
          this.onError(err);
        });
      });
    }
  }

  onError(error) {
    this.status = 'error';
    try {
      if (error.message === 'Invalid verification code provided, please try again.'
        || error.message === 'Invalid code provided, please request a code again.') {
        this.status = 'invalid';
        this.message = 'Invalid verification code provided. Please note that you must use the link in your most recent Linescape verification email. Verification codes from older emails are not valid. If you can\'t find the email, check your spam folder or re-send the code by clicking the button below.';
      } else if (error === 'There was a problem when we tried to send you a new email. Please try again') {
        this.status = 'invalid';
        this.message = 'There was a problem when we tried to send you a new email. Please try again';
      } else {
        this.status = 'invalid';
        this.message = `There was an error : ${error}`;
      }
    } catch (ex) {
      this.status = 'invalid';
      this.message = 'Invalid username and code combination provided, please request a code again.';
    }
  }

  resend() {
    this.status = 'resending';
    this.message = '';
    this.resendVerificationCode(this.email).then((result: boolean) => {
      this.verificationEmailResent(result);
    }).catch(err => {
      this.logger.log('confirm.service', 'resend error', err);
      this.onError(err);
    });
  }

  verificationEmailResent(result: boolean) {
    this.status = 'resent';
    if (!result) {
      this.onError('There was a problem when we tried to send you a new email. Please try again');
    } else {
      this.message = 'We sent you a new verification email.';
    }
  }

  resendVerificationCode(email: string) {
    if (this.type === 'register') {
      return new Promise((resolve, reject) => {
        this.auth.resendVerificationEmail(email).then(result => {
          resolve(result === 'SUCCESS');
        }).catch(err => reject(err));
      });
    } else {
      return new Promise((resolve, reject) => {
        this.auth.forgotPassword(email).then(result => {
          resolve(true);
        }).catch(err => reject(err));
      });
    }
  }
}



// WEBPACK FOOTER //
// ./node_modules/angular-router-loader/src!./node_modules/angular2-template-loader!./node_modules/@angularclass/hmr-loader!./node_modules/tslint-loader!./src/app/confirm/confirm.service.ts